import { useFlags } from 'launchdarkly-react-client-sdk';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { PropsWithChildren } from 'react';
import { ASSISTANT_FIELD_LABELS, ASSISTANT_EVENT_TYPES, ASSISTANT_ERROR } from '@brighte/validation';
import { datadogRum } from '@datadog/browser-rum';
import type { AssistantFormFieldInput } from '@graphql/generated/graphql';
import { useApplicationData } from '@provider';
import { IncomeVerificationErrorCode } from '../../steps/IncomeVerification/IncomeVerificationErrors';
import {
  ASSISTANT_ENABLED_STEPS,
  ASSISTANT_FLAG_KEY,
  ASSISTANT_SUB_STATES,
  ASSISTANT_FALLBACK_REPLY,
  ASSISTANT_RATE_LIMIT_REPLY,
  ASSISTANT_GREETING,
  ERROR_HANDOFF_QUESTIONS,
  JOURNAL_MAX_EVENTS,
  PROACTIVE_THRESHOLD,
  RAPID_CLICK_COUNT,
  RAPID_CLICK_WINDOW_MS,
  STRUGGLE_WEIGHTS,
} from './constants';
import type { AssistantSubState } from './constants';
import type { AssistantJournalEvent, AssistantMessage, AssistantSeededError } from './types';
import { useActiveStepKey } from './useActiveStepKey';
import { useAssistantChatMutation, useAssistantRateConversationMutation } from './useAssistantChat';
import { useIsAssistantViewport } from './useIsAssistantViewport';

export type AssistantRatingStage = 'none' | 'prompt' | 'thanks';

const nextMessageId = () => crypto.randomUUID();

/**
 * Unsaved form selections, keyed by the react-hook-form field path.
 *
 * Paths rather than named fields: the server decides what is acceptable by
 * reading the step's own Zod schema, honouring a path only when the assistant
 * may discuss it, it belongs to that step, and its type is a closed enum or
 * boolean. So reporting a value is always safe — anything that is not a
 * category is dropped server-side — and a new categorical question needs no
 * change here beyond reporting it.
 *
 * `undefined` retracts a path: it covers both "not answered yet" and "no longer
 * on screen", and is never transmitted.
 */
export type AssistantDraftSelections = Record<string, string | undefined>;

interface AssistantContextValue {
  isOpen: boolean;
  isLoading: boolean;
  send: (content: string, errorContext?: string) => boolean;
  stepKey: string | undefined;
  isEnabledOnStep: boolean;
  /** False below the assistant's minimum supported viewport — separate from step
   *  eligibility so a render gate can hide an armed rating prompt too. */
  isWideEnough: boolean;
  journal: AssistantJournalEvent[];
  /** Which part of the step is on screen, reported by the step itself. */
  subState: AssistantSubState | undefined;
  setSubState: (next: AssistantSubState | undefined) => void;
  struggleScore: number;
  offerVisible: boolean;
  seededError: AssistantSeededError | null;
  /** The conversation lives here so closing and reopening the panel keeps the thread. */
  messages: AssistantMessage[];
  appendMessage: (role: AssistantMessage['role'], content: string) => void;
  open: () => void;
  /** Closes the panel, prompting for a rating first if the conversation hasn't been rated/skipped yet. */
  close: () => void;
  /** `'none'` while chatting; `'prompt'`/`'thanks'` while the panel shows the rating footer instead of the composer. */
  ratingStage: AssistantRatingStage;
  submitRating: (rating: number) => void;
  skipRating: () => void;
  openWithError: (error: AssistantSeededError) => void;
  recordEvent: (event: AssistantJournalEvent) => void;
  /**
   * Report the customer's current, UNSAVED form selections so the assistant is
   * not blind to an answer that is on screen but not yet persisted. Steps call
   * this with react-hook-form field paths; the server validates each one against
   * the step schema and ignores anything that is not a category.
   *
   * Authoritative per path: a value sets it, `undefined` retracts it.
   */
  reportDraftSelections: (selections: AssistantDraftSelections) => void;
  registerFormReader: (reader: () => AssistantFormFieldInput[]) => () => void;
  /** Retract journalled validation failures the customer has since fixed. */
  resolveValidationFailures: (stepKey: string, stillFailing: string[]) => void;
  dismissOffer: () => void;
}

const AssistantContext = createContext<AssistantContextValue | null>(null);

/** Null when the provider (or the feature) is absent — integration points must not crash. */
export function useAssistantSafe(): AssistantContextValue | null {
  return useContext(AssistantContext);
}

export function useAssistant(): AssistantContextValue {
  const context = useContext(AssistantContext);
  if (!context) {
    throw new Error('useAssistant must be used within AssistantProvider');
  }
  return context;
}

/** Keep only allow-listed keys so a caller can never journal a typed value. */
const JOURNAL_FIELDS = new Set(Object.keys(ASSISTANT_FIELD_LABELS));
const JOURNAL_ERROR_CODES = new Set<string>([
  ...Object.values(IncomeVerificationErrorCode),
  'UPDATE_APPLICATION_ERROR',
  'UNAUTHORIZED',
  'UNAUTHENTICATED',
  'FORBIDDEN',
  'BAD_REQUEST',
  'INTERNAL_SERVER_ERROR',
]);

/** First code of a ` · `-joined chain — what the journal's allow-list is keyed on. */
function primaryErrorCode(message: string): string {
  return message.split(' · ')[0].trim();
}

function sanitizeEvent(event: AssistantJournalEvent): AssistantJournalEvent {
  return {
    type: event.type,
    ...(event.stepKey && ASSISTANT_ENABLED_STEPS.includes(event.stepKey) ? { stepKey: event.stepKey } : {}),
    ...(event.fieldName && JOURNAL_FIELDS.has(event.fieldName) ? { fieldName: event.fieldName } : {}),
    ...(event.errorCode
      ? { errorCode: JOURNAL_ERROR_CODES.has(event.errorCode) ? event.errorCode : 'UNKNOWN_ERROR' }
      : {}),
    ...(Number.isInteger(event.count) && event.count! >= 1 && event.count! <= 100 ? { count: event.count } : {}),
  };
}

export function AssistantProvider({ children }: PropsWithChildren<unknown>) {
  const stepKey = useActiveStepKey();
  const flags = useFlags<Record<string, boolean>>();
  const isWideEnough = useIsAssistantViewport();
  const enabled = Boolean(flags[ASSISTANT_FLAG_KEY]);
  // Viewport is part of "enabled on this step" rather than a separate render gate, so the
  // launcher, the panel and the error hand-off link all disappear together below the threshold.
  const isEnabledOnStep = Boolean(enabled && isWideEnough && stepKey && ASSISTANT_ENABLED_STEPS.includes(stepKey));
  // Mirrors `stepKey` while the assistant is enabled on it, synchronously during
  // render — so it still holds the step the conversation happened on even after
  // the customer has already navigated away and `stepKey` itself has moved on.
  // (Leaving the step unmounts/disables the assistant in the same render that
  // moves `stepKey`, so by the time any effect runs, the live value is too late.)
  const lastEnabledStepKey = useRef<string | undefined>();
  if (isEnabledOnStep) {
    lastEnabledStepKey.current = stepKey;
  }
  const { applicationId } = useApplicationData(['applicationId']);
  const chatMutation = useAssistantChatMutation();
  const rateMutation = useAssistantRateConversationMutation();
  const formScope = useMemo(() => ({ applicationId, stepKey, enabled }), [applicationId, stepKey, enabled]);
  const formScopes = useRef(new WeakSet<object>());
  const formReaders = useRef(new Map<symbol, { scope: object; read: () => AssistantFormFieldInput[] }>());
  const registerFormReader = useCallback(
    (read: () => AssistantFormFieldInput[]) => {
      const id = Symbol();
      formScopes.current.add(formScope);
      formReaders.current.set(id, { scope: formScope, read });
      return () => {
        formReaders.current.delete(id);
      };
    },
    [formScope],
  );
  /**
   * The customer's current answers, in the order the questions appear on screen.
   *
   * Order is part of the contract, not an accident: the assistant is told to point at the first
   * outstanding question rather than whichever it likes, which it can only do if the sequence is
   * meaningful. Readers register on mount, so the Map's insertion order follows the React tree,
   * and re-setting an existing key keeps its original position.
   */
  const readFormContext = useCallback(() => {
    if (!formScopes.current.has(formScope)) return undefined;
    const fields = new Map<string, AssistantFormFieldInput>();
    for (const reader of formReaders.current.values()) {
      if (reader.scope !== formScope) continue;
      for (const field of reader.read()) fields.set(field.fieldName, field);
    }
    return [...fields.values()].slice(0, 50);
  }, [formScope]);
  const [isLoading, setIsLoading] = useState(false);
  const pending = useRef(false);
  const generation = useRef(0);
  const conversationId = useRef<string>();
  const [pendingErrors, setPendingErrors] = useState<(AssistantSeededError & { generation: number })[]>([]);

  const [isOpen, setIsOpen] = useState(false);
  const [ratingStage, setRatingStage] = useState<AssistantRatingStage>('none');
  // Asked at most once per conversation, whether rated or skipped.
  const promptedConversations = useRef(new Set<string>());
  // The "thanks" → close delay after submitRating. Cleared on unmount, on the
  // app/enabled reset, and whenever finalizeClose runs early (e.g. the
  // customer dismisses while "thanks" is still showing) — otherwise a stale
  // timer fires finalizeClose again later, possibly against an unmounted tree.
  const ratingCloseTimer = useRef<number>();
  // The step the conversation happened on, captured when the rating prompt
  // appears — `stepKey` itself may already point at the destination step by
  // then (step-exit triggers this prompt after the step has changed), so
  // submitRating must not read the live value.
  const ratingStepKey = useRef<string | undefined>();
  const stepExitHandled = useRef(true);
  // State rather than a ref, unlike the form readers above: the panel has to re-render when
  // this changes so the suggested questions follow the screen. It is one short allow-listed
  // string, so that re-render stays cheap.
  const [subState, setSubStateValue] = useState<AssistantSubState | undefined>();
  const [journal, setJournal] = useState<AssistantJournalEvent[]>([]);
  // Held in a ref, not state: it changes on every keystroke/selection and only
  // ever needs reading at send time, so re-rendering the whole form tree for it
  // would be pure waste.
  const draftSelections = useRef<Record<string, string>>({});
  const [messages, setMessages] = useState<AssistantMessage[]>([
    { id: nextMessageId(), role: 'assistant', content: ASSISTANT_GREETING },
  ]);

  const appendMessage = useCallback((role: AssistantMessage['role'], content: string) => {
    setMessages(current => [...current, { id: nextMessageId(), role, content }]);
  }, []);
  const [seededError, setSeededError] = useState<AssistantSeededError | null>(null);
  // Proactive-offer bookkeeping: at most one offer per step, dismiss silences
  // the offer for the whole session.
  const [offeredSteps, setOfferedSteps] = useState<string[]>([]);
  const [offerDismissed, setOfferDismissed] = useState(false);
  const [openedEver, setOpenedEver] = useState(false);

  const setSubState = useCallback(
    (next: AssistantSubState | undefined) => {
      if (!isEnabledOnStep) return;
      const known = next && (Object.values(ASSISTANT_SUB_STATES) as string[]).includes(next);
      setSubStateValue(known ? next : undefined);
    },
    [isEnabledOnStep],
  );

  const recordEvent = useCallback(
    (event: AssistantJournalEvent) => {
      if (!isEnabledOnStep || !ASSISTANT_EVENT_TYPES.includes(event.type)) return;
      const sanitized = sanitizeEvent(event);
      setJournal(current => [...current, sanitized].slice(-JOURNAL_MAX_EVENTS));
    },
    [isEnabledOnStep],
  );

  const reportDraftSelections = useCallback((selections: AssistantDraftSelections) => {
    // Each report is AUTHORITATIVE for the paths it names: a value sets that
    // path, and `undefined` removes it. Removing rather than ignoring matters in
    // both directions — an unanswered field on mount must not create a key (or
    // the "send only when non-empty" check below would ship an empty
    // `draftSelections`), and an answer the customer has since abandoned must
    // not keep riding along. A pension type chosen and then dropped by switching
    // to Employed would otherwise have the assistant recommend a pension
    // document to someone who is employed.
    const next = { ...draftSelections.current };
    for (const [path, value] of Object.entries(selections)) {
      if (value === undefined) delete next[path];
      else next[path] = value;
    }
    draftSelections.current = next;
  }, []);

  /**
   * A journalled failure is a point-in-time fact with no timestamp attached, so
   * nothing would ever retract it once the customer fixes the field and the
   * assistant keeps reporting a problem that no longer exists. Each submit
   * reports which fields are STILL failing, and every other journalled failure
   * for that step is dropped — a step can fail on one field while another was
   * corrected, so clearing only on a wholly valid submit is not enough.
   */
  const resolveValidationFailures = useCallback((step: string, stillFailing: string[]) => {
    // The journal stores the raw dotted path, so these compare directly. An
    // event whose fieldName was dropped by the allow-list names no field and is
    // left alone — there is nothing to match it against.
    const failing = new Set(stillFailing);
    setJournal(current =>
      current.filter(
        event =>
          !(
            event.type === 'validation_failed' &&
            event.stepKey === step &&
            event.fieldName !== undefined &&
            !failing.has(event.fieldName)
          ),
      ),
    );
  }, []);

  // Struggle score counts only the current step's friction.
  const struggleScore = useMemo(
    () =>
      journal
        .filter(event => !stepKey || !event.stepKey || event.stepKey === stepKey)
        .reduce((score, event) => score + (STRUGGLE_WEIGHTS[event.type] ?? 0), 0),
    [journal, stepKey],
  );

  const [offerActive, setOfferActive] = useState(false);

  // Arm the offer the first time the score crosses the threshold on a step;
  // it stays visible until opened or dismissed, and never re-arms on that step.
  useEffect(() => {
    if (
      isEnabledOnStep &&
      !isOpen &&
      !offerDismissed &&
      !openedEver &&
      struggleScore >= PROACTIVE_THRESHOLD &&
      stepKey &&
      !offeredSteps.includes(stepKey)
    ) {
      setOfferedSteps(current => [...current, stepKey]);
      setOfferActive(true);
      datadogRum.addAction('Assistant proactive offer shown', { stepKey, struggleScore });
    }
  }, [struggleScore, isEnabledOnStep, isOpen, offerDismissed, openedEver, stepKey, offeredSteps]);

  // Leaving the step withdraws a still-armed offer.
  useEffect(() => {
    setOfferActive(false);
  }, [stepKey]);

  const offerVisible = offerActive && !isOpen && !offerDismissed;

  // Rapid-click detection, only while the assistant is live on this step.
  const clickTracker = useRef<{ target: string; times: number[] }>({ target: '', times: [] });
  useEffect(() => {
    if (!isEnabledOnStep) {
      return;
    }
    const onClick = (event: MouseEvent) => {
      const element = event.target as HTMLElement | null;
      // Identify the target coarsely — tag + id/name only, never text content.
      const target = element ? `${element.tagName}#${element.id || (element as HTMLInputElement).name || ''}` : '';
      const now = Date.now();
      const tracker = clickTracker.current;
      const times = tracker.target === target ? tracker.times.filter(t => now - t < RAPID_CLICK_WINDOW_MS) : [];
      times.push(now);
      clickTracker.current = { target, times };
      if (times.length >= RAPID_CLICK_COUNT) {
        clickTracker.current = { target, times: [] };
        recordEvent({ type: 'rapid_clicks', stepKey, count: times.length });
      }
    };
    window.addEventListener('click', onClick);
    return () => window.removeEventListener('click', onClick);
  }, [isEnabledOnStep, stepKey, recordEvent]);

  const open = useCallback(() => {
    if (!isEnabledOnStep) return;
    setIsOpen(true);
    setOpenedEver(true);
    setOfferActive(false);
    datadogRum.addAction('Assistant opened', { stepKey });
  }, [stepKey, isEnabledOnStep]);

  const finalizeClose = useCallback(() => {
    if (ratingCloseTimer.current !== undefined) {
      window.clearTimeout(ratingCloseTimer.current);
      ratingCloseTimer.current = undefined;
    }
    setIsOpen(false);
    setSeededError(null);
    setRatingStage('none');
    // Otherwise a second exit before the panel reopens would be missed (nothing else resets this).
    stepExitHandled.current = true;
  }, []);

  /**
   * Shared gate for every way the panel can close while chatting — the
   * explicit × / Esc / outside dismiss, and the customer finishing the step
   * the conversation happened on (e.g. Save and continue). A conversation
   * with at least one exchange that hasn't been prompted yet is asked to
   * rate first instead, and the panel stays open showing the rating prompt
   * until the customer rates or skips; `promptedConversations` then stops it
   * asking again for that same conversation, however it's closed next.
   */
  const requestClose = useCallback(() => {
    const conversationStarted = messages.some(message => message.role === 'user');
    const activeConversationId = conversationId.current;
    if (conversationStarted && activeConversationId && !promptedConversations.current.has(activeConversationId)) {
      promptedConversations.current.add(activeConversationId);
      ratingStepKey.current = lastEnabledStepKey.current;
      setRatingStage('prompt');
      return;
    }
    finalizeClose();
  }, [messages, finalizeClose]);

  const close = useCallback(() => {
    requestClose();
  }, [requestClose]);

  const submitRating = useCallback(
    (rating: number) => {
      // Guards against a rapid double-tap firing the mutation twice and
      // leaking the first close timer before the "thanks" state renders.
      if (rateMutation.isLoading) return;
      const activeConversationId = conversationId.current;
      if (!activeConversationId || !applicationId) {
        finalizeClose();
        return;
      }
      // Stops a late response from reopening "thanks" after an app/enabled reset.
      const requestGeneration = generation.current;
      rateMutation.mutate(
        {
          input: {
            applicationId,
            conversationId: activeConversationId,
            stepKey: ratingStepKey.current,
            rating,
          },
        },
        {
          // Only claim success, and only start the auto-close timer, once the
          // mutation actually lands — otherwise a failed request still shows
          // "Thanks" and the rating is silently lost.
          onSuccess: () => {
            if (requestGeneration !== generation.current) return;
            setRatingStage('thanks');
            ratingCloseTimer.current = window.setTimeout(finalizeClose, 1500);
          },
          // Close as if skipped, rather than leaving the prompt frozen on failure.
          onError: () => {
            if (requestGeneration !== generation.current) return;
            finalizeClose();
          },
        },
      );
    },
    [applicationId, rateMutation, finalizeClose],
  );

  const skipRating = useCallback(() => {
    finalizeClose();
  }, [finalizeClose]);

  // Belt-and-braces for true provider unmount (route change, etc.) — the reset
  // effect above only clears the timer on an [enabled, applicationId] change.
  useEffect(() => {
    return () => {
      if (ratingCloseTimer.current !== undefined) window.clearTimeout(ratingCloseTimer.current);
    };
  }, []);

  const openWithError = useCallback(
    (error: AssistantSeededError) => {
      if (!isEnabledOnStep) return;
      setSeededError(error);
      setPendingErrors(current => [...current, { ...error, generation: generation.current }]);
      // Journal the primary code only. `message` may be several codes joined for the backend
      // catalogue, and the whole string would miss the allow-list and record as UNKNOWN_ERROR.
      recordEvent({ type: 'error_explained', stepKey: error.stepKey, errorCode: primaryErrorCode(error.message) });
      setIsOpen(true);
      setOpenedEver(true);
      datadogRum.addAction('Assistant opened from error', { stepKey: error.stepKey });
    },
    [recordEvent, isEnabledOnStep],
  );

  const dismissOffer = useCallback(() => {
    if (!isEnabledOnStep) return;
    setOfferDismissed(true);
    setOfferActive(false);
    datadogRum.addAction('Assistant proactive offer dismissed', { stepKey });
  }, [stepKey, isEnabledOnStep]);

  useEffect(() => {
    if (ratingCloseTimer.current !== undefined) {
      window.clearTimeout(ratingCloseTimer.current);
      ratingCloseTimer.current = undefined;
    }
    generation.current++;
    conversationId.current = undefined;
    pending.current = false;
    setIsLoading(false);
    setIsOpen(false);
    setJournal([]);
    setMessages([{ id: nextMessageId(), role: 'assistant', content: ASSISTANT_GREETING }]);
    setSeededError(null);
    setPendingErrors([]);
    setOfferActive(false);
    setOfferedSteps([]);
    setOfferDismissed(false);
    setOpenedEver(false);
    setRatingStage('none');
    clickTracker.current = { target: '', times: [] };
    draftSelections.current = {};
    promptedConversations.current = new Set();
    ratingStepKey.current = undefined;
    lastEnabledStepKey.current = undefined;
  }, [enabled, applicationId]);

  // Leaving the step (e.g. Save and continue) unmounts the assistant, so trigger
  // the rating gate once on exit rather than never — `stepExitHandled` stops
  // it re-firing. An explicit panel close goes through the same gate via `close`.
  //
  // isEnabledOnStep also goes false on viewport narrowing, which must hard-close
  // (finalizeClose) rather than arm a rating prompt (requestClose).
  useEffect(() => {
    if (isEnabledOnStep) {
      stepExitHandled.current = false;
      return;
    }
    if (!isWideEnough) {
      stepExitHandled.current = true;
      finalizeClose();
      return;
    }
    if (!stepExitHandled.current && isOpen) {
      stepExitHandled.current = true;
      requestClose();
    }
  }, [isEnabledOnStep, isWideEnough, isOpen, requestClose, finalizeClose]);

  const send = useCallback(
    (content: string, errorContext?: string) => {
      if (!isEnabledOnStep || !applicationId || !content.trim() || content.trim().length > 2000 || pending.current)
        return false;
      pending.current = true;
      setIsLoading(true);
      const requestGeneration = generation.current;
      const trimmed = content.trim();
      appendMessage('user', trimmed);
      const finish = () => {
        if (requestGeneration !== generation.current) return false;
        pending.current = false;
        setIsLoading(false);
        return true;
      };
      const formContext = readFormContext();
      const buildInput = () => ({
        applicationId,
        stepKey: stepKey!,
        message: trimmed,
        requestId: crypto.randomUUID(),
        ...(conversationId.current ? { conversationId: conversationId.current } : {}),
        ...(errorContext ? { errorContext } : {}),
        sessionContext: journal,
        ...(formContext !== undefined ? { formContext } : {}),
        ...(Object.keys(draftSelections.current).length
          ? {
              draftSelections: Object.entries(draftSelections.current).map(([fieldName, value]) => ({
                fieldName,
                value,
              })),
            }
          : {}),
      });
      // A Redis-expired conversationId is a housekeeping detail, not something the customer
      // caused or needs to act on — clear it and silently resend as a new conversation once,
      // rather than surfacing it as an error. Only a second failure (of any kind) is shown.
      const attempt = (isRetry: boolean) => {
        chatMutation.mutate(
          { input: buildInput() },
          {
            onSuccess: data => {
              if (!finish()) return;
              conversationId.current = data.assistantChat.conversationId ?? undefined;
              appendMessage('assistant', data.assistantChat.reply);
            },
            onError: error => {
              const code =
                (error as { response?: { errors?: { message?: string }[] } })?.response?.errors?.[0]?.message ??
                (error instanceof Error ? error.message : '');
              if (!isRetry && code.includes(ASSISTANT_ERROR.expired)) {
                conversationId.current = undefined;
                datadogRum.addAction('Assistant conversation silently resumed after expiry', { stepKey });
                attempt(true);
                return;
              }
              if (!finish()) return;
              appendMessage(
                'assistant',
                code.includes(ASSISTANT_ERROR.rateLimited) ? ASSISTANT_RATE_LIMIT_REPLY : ASSISTANT_FALLBACK_REPLY,
              );
            },
          },
        );
      };
      attempt(false);
      return true;
    },
    [isEnabledOnStep, applicationId, stepKey, journal, appendMessage, chatMutation, readFormContext],
  );

  useEffect(() => {
    const next = pendingErrors[0];
    if (
      next &&
      next.generation === generation.current &&
      send(next.question ?? ERROR_HANDOFF_QUESTIONS.default, next.message)
    ) {
      setPendingErrors(current => current.slice(1));
    }
  }, [pendingErrors, send, isLoading]);

  const value = useMemo(
    () => ({
      isOpen,
      isLoading,
      send,
      stepKey,
      isEnabledOnStep,
      isWideEnough,
      journal,
      subState,
      setSubState,
      struggleScore,
      offerVisible,
      seededError,
      messages,
      appendMessage,
      open,
      close,
      ratingStage,
      submitRating,
      skipRating,
      openWithError,
      recordEvent,
      reportDraftSelections,
      registerFormReader,
      resolveValidationFailures,
      dismissOffer,
    }),
    [
      isOpen,
      isLoading,
      send,
      stepKey,
      isEnabledOnStep,
      isWideEnough,
      journal,
      subState,
      setSubState,
      struggleScore,
      offerVisible,
      seededError,
      messages,
      appendMessage,
      open,
      close,
      ratingStage,
      submitRating,
      skipRating,
      openWithError,
      recordEvent,
      reportDraftSelections,
      registerFormReader,
      resolveValidationFailures,
      dismissOffer,
    ],
  );

  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>;
}
